import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DayTableSimple from '../DayTableSimple';
import ScheduleEntry from '../../models/ScheduleEntry';
import { SharedScrollProvider } from '../../contexts/SharedScrollContext';
import { KEY_SEPARATOR } from '../../hooks/useScheduleDragAndDrop';
import { createEmptyDraggedEntry } from '../../types/dragAndDrop';
import type Teacher from '../../models/Teacher';
import type Group from '../../models/Group';
import type Subject from '../../models/Subject';
import type Room from '../../models/Room';
import { formatDraftOptionLabel } from '../../utils/scheduleDraftKind';
import { formatPeriodDropdownLabel } from '../../utils/yearStructurePeriodUtils';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
    UNSCOPED_PERIOD_KEY,
    getDraftPeriodId,
    getPeriodKey,
    periodKeysEqual,
    draftMatchesPeriodKey,
    isDraftLiveForPeriod,
    pickDraftNameForPeriod,
} from '../../utils/copyDraftViewUtils';

const EMPTY_DRAG_INFO = createEmptyDraggedEntry();
const MARQUEE_DRAG_THRESHOLD_PX = 5;

interface MarqueeState {
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
}

const normalizeClientRect = (x1: number, y1: number, x2: number, y2: number) => ({
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    right: Math.max(x1, x2),
    bottom: Math.max(y1, y2),
});

const rectsIntersect = (
    a: { left: number; top: number; right: number; bottom: number },
    b: { left: number; top: number; right: number; bottom: number }
) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

const findEntryIdsInClientRect = (container: HTMLElement, selectRect: ReturnType<typeof normalizeClientRect>) => {
    const ids: string[] = [];
    container.querySelectorAll('[data-schedule-entry-id]').forEach((element) => {
        const entryId = element.getAttribute('data-schedule-entry-id');
        if (!entryId) return;
        const rect = element.getBoundingClientRect();
        if (rectsIntersect(rect, selectRect)) {
            ids.push(entryId);
        }
    });
    return ids;
};

interface DraftOption {
    id?: string | number | null;
    draft_name: string;
    display_name?: string | null;
    version_number?: number | null;
    period_id?: string | number | null;
    periodId?: string | number | null;
}

interface TimePeriodOption {
    id: string | number;
    name?: string | null;
    color?: string | null;
    active_schedule_draft_id?: string | number | null;
    activeScheduleDraftId?: string | number | null;
    start_week?: number | null;
    startWeek?: number | null;
    end_week?: number | null;
    endWeek?: number | null;
    year?: number | null;
    start_date?: string | null;
    end_date?: string | null;
    startDate?: string | null;
    endDate?: string | null;
}

interface CopyHistoryBatch {
    targetDraftName: string;
    sourceDraftName: string;
    entries: ScheduleEntry[];
    deletedSourceEntries: ScheduleEntry[];
}

interface CopyDraftViewProps {
    assetManager: {
        fetchScheduleEntriesForDraft: (draftName: string) => Promise<ScheduleEntry[]>;
        createScheduleEntry: (data: Record<string, unknown>) => Promise<ScheduleEntry>;
        deleteScheduleEntry: (entryId: string | number) => Promise<void>;
    };
    drafts: DraftOption[];
    timePeriods?: TimePeriodOption[];
    viewPeriodId?: string | number | null;
    currentDraftName: string | null;
    activeDays: string[];
    allRoomsForGrid: Room[];
    user: { role: string };
    teachers: Teacher[];
    groups: Group[];
    subjects: Subject[];
    selectedWeek?: { week: number; year?: number } | null;
    selectedYearForView?: number;
    getViewFilteredEntries: (
        entries: ScheduleEntry[],
        options?: { skipPeriodScope?: boolean }
    ) => ScheduleEntry[];
    onCopyToCurrentDraft?: (entry: ScheduleEntry) => void;
    onRemoveFromCurrentDraft?: (entryId: string | number) => void;
    onError?: (message: string) => void;
    onSuccess?: (message: string) => void;
}

type PanelSide = 'left' | 'right';

const duplicateEntryForDraft = (
    sourceEntry: ScheduleEntry,
    targetDraftName: string,
    targetPeriodId: string | number | null
): ScheduleEntry => {
    const sourceObj = typeof sourceEntry?.toObject === 'function'
        ? sourceEntry.toObject()
        : { ...(sourceEntry as unknown as Record<string, unknown>) };
    const newItemId = `item-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    const newItemData = {
        ...sourceObj,
        id: newItemId,
        draft_name: targetDraftName,
        periodId: targetPeriodId,
        period_id: targetPeriodId,
    };
    return new ScheduleEntry(newItemData);
};

const isInvalidRoomError = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    return /invalid room_id/i.test(message);
};

const stripRoomIdFromCreateData = (createData: Record<string, unknown>) => {
    const next = { ...createData };
    delete next.room_id;
    if (next.room && typeof next.room === 'object') {
        const room = { ...(next.room as Record<string, unknown>) };
        delete room.id;
        next.room = room;
    }
    return next;
};

const createCopiedScheduleEntry = async (
    assetManager: CopyDraftViewProps['assetManager'],
    createData: Record<string, unknown>
) => {
    try {
        return await assetManager.createScheduleEntry(createData);
    } catch (error) {
        if (!isInvalidRoomError(error)) throw error;
        return assetManager.createScheduleEntry(stripRoomIdFromCreateData(createData));
    }
};

const recreateEntryOnDraft = async (
    assetManager: CopyDraftViewProps['assetManager'],
    sourceEntry: ScheduleEntry,
    targetDraftName: string,
    targetPeriodId?: string | number | null
) => {
    const createData = sourceEntry.toObject() as Record<string, unknown>;
    delete createData.id;
    createData.draft_name = targetDraftName;
    if (targetPeriodId !== undefined) {
        createData.periodId = targetPeriodId;
        createData.period_id = targetPeriodId;
    }
    return createCopiedScheduleEntry(assetManager, createData);
};

const CopyDraftView: React.FC<CopyDraftViewProps> = ({
    assetManager,
    drafts,
    timePeriods = [],
    viewPeriodId = null,
    currentDraftName,
    activeDays,
    allRoomsForGrid,
    user,
    teachers,
    groups,
    subjects,
    selectedWeek,
    selectedYearForView,
    getViewFilteredEntries,
    onCopyToCurrentDraft,
    onRemoveFromCurrentDraft,
    onError,
    onSuccess,
}) => {
    const draftOptions = useMemo(
        () => (drafts || []).filter((draft) => Boolean(draft?.draft_name)),
        [drafts]
    );
    const getDraftLabel = useCallback((draftName: string) => (
        formatDraftOptionLabel(draftOptions.find((draft) => draft.draft_name === draftName)) || draftName
    ), [draftOptions]);

    const [leftDraftName, setLeftDraftName] = useState<string>(() => (
        currentDraftName || draftOptions[0]?.draft_name || ''
    ));
    const [rightDraftName, setRightDraftName] = useState<string>(() => {
        const currentDraft = draftOptions.find((draft) => draft.draft_name === currentDraftName);
        const currentPeriodKey = getPeriodKey(getDraftPeriodId(currentDraft));
        const samePeriodAlt = draftOptions.find((draft) => (
            draft.draft_name !== currentDraftName
            && draftMatchesPeriodKey(draft, currentPeriodKey)
        ));
        const fallback = samePeriodAlt?.draft_name
            || draftOptions.find((draft) => draft.draft_name !== currentDraftName)?.draft_name
            || draftOptions[1]?.draft_name
            || draftOptions[0]?.draft_name
            || '';
        return fallback;
    });

    const [leftEntries, setLeftEntries] = useState<ScheduleEntry[]>([]);
    const [rightEntries, setRightEntries] = useState<ScheduleEntry[]>([]);
    const [leftSelectedIds, setLeftSelectedIds] = useState<string[]>([]);
    const [leftLoading, setLeftLoading] = useState(false);
    const [rightLoading, setRightLoading] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [isHistoryAction, setIsHistoryAction] = useState(false);
    const [undoStack, setUndoStack] = useState<CopyHistoryBatch[]>([]);
    const [redoStack, setRedoStack] = useState<CopyHistoryBatch[]>([]);
    const [highlightedItemId, setHighlightedItemId] = useState<string | number | null>(null);
    const [marquee, setMarquee] = useState<MarqueeState | null>(null);
    const [showCrossPeriodConfirm, setShowCrossPeriodConfirm] = useState(false);
    const leftPanelScrollRef = useRef<HTMLDivElement>(null);
    const rightPanelScrollRef = useRef<HTMLDivElement>(null);
    const isSyncingVerticalScrollRef = useRef(false);
    const marqueeActiveRef = useRef(false);

    const handlePanelScroll = useCallback((source: PanelSide) => {
        if (isSyncingVerticalScrollRef.current) return;

        const sourceEl = source === 'left' ? leftPanelScrollRef.current : rightPanelScrollRef.current;
        const targetEl = source === 'left' ? rightPanelScrollRef.current : leftPanelScrollRef.current;
        if (!sourceEl || !targetEl) return;

        isSyncingVerticalScrollRef.current = true;
        targetEl.scrollTop = sourceEl.scrollTop;
        requestAnimationFrame(() => {
            isSyncingVerticalScrollRef.current = false;
        });
    }, []);

    useEffect(() => {
        if (!leftDraftName && draftOptions[0]?.draft_name) {
            setLeftDraftName(draftOptions[0].draft_name);
        }
        if (!rightDraftName && draftOptions.length > 1) {
            const alt = draftOptions.find((draft) => draft.draft_name !== leftDraftName);
            if (alt?.draft_name) setRightDraftName(alt.draft_name);
        }
    }, [draftOptions, leftDraftName, rightDraftName]);

    const loadDraftEntries = useCallback(async (draftName: string, side: PanelSide) => {
        if (!draftName || !assetManager?.fetchScheduleEntriesForDraft) return;
        const setLoading = side === 'left' ? setLeftLoading : setRightLoading;
        const setEntries = side === 'left' ? setLeftEntries : setRightEntries;
        setLoading(true);
        try {
            const entries = await assetManager.fetchScheduleEntriesForDraft(draftName);
            const processed = (entries || []).map((entry) => {
                const entryData = typeof entry.toObject === 'function' ? entry.toObject() : { ...entry };
                return new ScheduleEntry({
                    ...entryData,
                    weekSeries: entryData.weekSeries || '',
                });
            });
            setEntries(processed);
        } catch (error) {
            console.error('[CopyDraftView] Failed to load draft entries:', error);
            onError?.(`Failed to load "${getDraftLabel(draftName)}"`);
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [assetManager, getDraftLabel, onError]);

    useEffect(() => {
        if (leftDraftName) {
            loadDraftEntries(leftDraftName, 'left');
            setLeftSelectedIds([]);
        }
    }, [leftDraftName, loadDraftEntries]);

    useEffect(() => {
        if (rightDraftName) {
            loadDraftEntries(rightDraftName, 'right');
        }
    }, [rightDraftName, loadDraftEntries]);

    useEffect(() => {
        setUndoStack([]);
        setRedoStack([]);
        setShowCrossPeriodConfirm(false);
    }, [leftDraftName, rightDraftName]);

    useEffect(() => {
        if (leftLoading || rightLoading) return;
        const leftEl = leftPanelScrollRef.current;
        const rightEl = rightPanelScrollRef.current;
        if (!leftEl || !rightEl) return;
        rightEl.scrollTop = leftEl.scrollTop;
    }, [leftLoading, rightLoading, leftDraftName, rightDraftName]);

    const leftDraft = useMemo(
        () => draftOptions.find((draft) => draft.draft_name === leftDraftName) || null,
        [draftOptions, leftDraftName]
    );
    const rightDraft = useMemo(
        () => draftOptions.find((draft) => draft.draft_name === rightDraftName) || null,
        [draftOptions, rightDraftName]
    );
    const leftPeriodId = getDraftPeriodId(leftDraft);
    const rightPeriodId = getDraftPeriodId(rightDraft);
    const isCrossPeriodCopy = Boolean(leftDraft && rightDraft && !periodKeysEqual(leftPeriodId, rightPeriodId));
    const isSourceLive = isDraftLiveForPeriod(leftDraft, timePeriods);

    const periodOptions = useMemo(() => {
        const keysWithDrafts = new Set(draftOptions.map((draft) => getPeriodKey(getDraftPeriodId(draft))));
        const options = (timePeriods || [])
            .filter((period) => period?.id != null && keysWithDrafts.has(String(period.id)))
            .map((period) => ({
                id: String(period.id),
                label: formatPeriodDropdownLabel(period),
            }));

        if (keysWithDrafts.has(UNSCOPED_PERIOD_KEY)) {
            options.push({ id: UNSCOPED_PERIOD_KEY, label: 'Unscoped' });
        }

        [leftPeriodId, rightPeriodId].forEach((periodId) => {
            const key = getPeriodKey(periodId);
            if (options.some((option) => option.id === key)) return;
            if (key === UNSCOPED_PERIOD_KEY) {
                options.push({ id: UNSCOPED_PERIOD_KEY, label: 'Unscoped' });
                return;
            }
            const period = (timePeriods || []).find((item) => String(item.id) === key);
            options.push({
                id: key,
                label: period ? formatPeriodDropdownLabel(period) : `Period ${key}`,
            });
        });

        return options;
    }, [draftOptions, timePeriods, leftPeriodId, rightPeriodId]);

    const draftsByPeriodKey = useCallback((periodKey: string) => (
        draftOptions.filter((draft) => draftMatchesPeriodKey(draft, periodKey))
    ), [draftOptions]);

    const applyPanelEntriesFilter = useCallback((entries: ScheduleEntry[], draftPeriodId: string | number | null) => {
        const skipPeriodScope = !periodKeysEqual(draftPeriodId, viewPeriodId);
        return getViewFilteredEntries(entries || [], { skipPeriodScope });
    }, [getViewFilteredEntries, viewPeriodId]);

    const leftFilteredEntries = useMemo(
        () => applyPanelEntriesFilter(leftEntries, leftPeriodId),
        [applyPanelEntriesFilter, leftEntries, leftPeriodId]
    );
    const rightFilteredEntries = useMemo(
        () => applyPanelEntriesFilter(rightEntries, rightPeriodId),
        [applyPanelEntriesFilter, rightEntries, rightPeriodId]
    );

    const leftSelectedEntries = useMemo(
        () => {
            const selectedSet = new Set(leftSelectedIds);
            return leftFilteredEntries.filter((entry) => selectedSet.has(String(entry.id)));
        },
        [leftFilteredEntries, leftSelectedIds]
    );

    const persistCopiedEntry = useCallback(async (
        sourceEntry: ScheduleEntry,
        targetDraftName: string,
        targetPeriodId: string | number | null,
        options: { notify?: boolean } = {}
    ) => {
        const { notify = true } = options;
        const localDuplicate = duplicateEntryForDraft(sourceEntry, targetDraftName, targetPeriodId);
        const createData = localDuplicate.toObject();
        delete (createData as Record<string, unknown>).id;

        const savedEntry = await createCopiedScheduleEntry(
            assetManager,
            createData as Record<string, unknown>
        );
        setRightEntries((prev) => [...prev, savedEntry]);

        if (currentDraftName && targetDraftName === currentDraftName) {
            onCopyToCurrentDraft?.(savedEntry);
        }

        if (notify) {
            setHighlightedItemId(savedEntry.id);
            window.setTimeout(() => setHighlightedItemId(null), 2000);
            onSuccess?.(`Copied entry to "${getDraftLabel(targetDraftName)}"`);
        }
        return savedEntry;
    }, [assetManager, currentDraftName, getDraftLabel, onCopyToCurrentDraft, onSuccess]);

    const deleteSourceEntries = useCallback(async (entries: ScheduleEntry[], sourceDraftName: string) => {
        const deleted: ScheduleEntry[] = [];
        for (const entry of entries) {
            await assetManager.deleteScheduleEntry(entry.id);
            deleted.push(entry);
            if (sourceDraftName === leftDraftName) {
                setLeftEntries((prev) => prev.filter((item) => String(item.id) !== String(entry.id)));
            }
            if (currentDraftName && sourceDraftName === currentDraftName) {
                onRemoveFromCurrentDraft?.(entry.id);
            }
        }
        return deleted;
    }, [assetManager, leftDraftName, currentDraftName, onRemoveFromCurrentDraft]);

    const restoreSourceEntries = useCallback(async (entries: ScheduleEntry[], sourceDraftName: string) => {
        const restored: ScheduleEntry[] = [];
        for (const entry of entries) {
            const savedEntry = await recreateEntryOnDraft(assetManager, entry, sourceDraftName);
            restored.push(savedEntry);
            if (sourceDraftName === leftDraftName) {
                setLeftEntries((prev) => [...prev, savedEntry]);
            }
            if (currentDraftName && sourceDraftName === currentDraftName) {
                onCopyToCurrentDraft?.(savedEntry);
            }
        }
        return restored;
    }, [assetManager, leftDraftName, currentDraftName, onCopyToCurrentDraft]);

    const performCopyLeftToRight = useCallback(async (deleteSource: boolean) => {
        if (leftSelectedEntries.length === 0 || !rightDraftName || isCopying || isHistoryAction) return;
        setIsCopying(true);
        setShowCrossPeriodConfirm(false);
        try {
            const savedEntries: ScheduleEntry[] = [];
            const failedCopies: { id: string; message: string }[] = [];
            for (const entry of leftSelectedEntries) {
                try {
                    const savedEntry = await persistCopiedEntry(
                        entry,
                        rightDraftName,
                        rightPeriodId,
                        { notify: false }
                    );
                    savedEntries.push(savedEntry);
                } catch (entryError) {
                    failedCopies.push({
                        id: String(entry.id),
                        message: entryError instanceof Error ? entryError.message : String(entryError),
                    });
                }
            }

            if (savedEntries.length === 0) {
                throw new Error(failedCopies[0]?.message || 'Failed to copy entries to the target draft');
            }

            let deletedSourceEntries: ScheduleEntry[] = [];
            if (deleteSource) {
                const copiedSourceEntries = leftSelectedEntries.filter((entry) => (
                    !failedCopies.some((failed) => failed.id === String(entry.id))
                ));
                try {
                    deletedSourceEntries = await deleteSourceEntries(copiedSourceEntries, leftDraftName);
                } catch (deleteError) {
                    console.error('[CopyDraftView] Copied entries but failed to delete source:', deleteError);
                    onError?.('Copied to the target version, but could not delete the source entries');
                }
            }

            const lastSaved = savedEntries[savedEntries.length - 1];
            if (lastSaved) {
                setHighlightedItemId(lastSaved.id);
                window.setTimeout(() => setHighlightedItemId(null), 2000);
            }
            setUndoStack((prev) => [...prev, {
                targetDraftName: rightDraftName,
                sourceDraftName: leftDraftName,
                entries: savedEntries,
                deletedSourceEntries,
            }]);
            setRedoStack([]);
            const count = savedEntries.length;
            const copiedLabel = `Copied ${count} ${count === 1 ? 'entry' : 'entries'} to "${getDraftLabel(rightDraftName)}"`;
            if (failedCopies.length > 0) {
                onError?.(`${copiedLabel}. ${failedCopies.length} could not be copied: ${failedCopies[0]?.message || 'unknown error'}`);
            } else {
                onSuccess?.(deletedSourceEntries.length > 0
                    ? `${copiedLabel} and deleted them from the source version`
                    : copiedLabel);
            }
            setLeftSelectedIds([]);
        } catch (error) {
            console.error('[CopyDraftView] Copy left-to-right failed:', error);
            onError?.('Failed to copy entries to the target draft');
        } finally {
            setIsCopying(false);
        }
    }, [
        leftSelectedEntries,
        rightDraftName,
        rightPeriodId,
        isCopying,
        isHistoryAction,
        persistCopiedEntry,
        deleteSourceEntries,
        leftDraftName,
        getDraftLabel,
        onError,
        onSuccess,
    ]);

    const handleCopyLeftToRight = useCallback(() => {
        if (leftSelectedEntries.length === 0 || !rightDraftName || isCopying || isHistoryAction) return;
        if (isCrossPeriodCopy) {
            setShowCrossPeriodConfirm(true);
            return;
        }
        void performCopyLeftToRight(false);
    }, [
        leftSelectedEntries.length,
        rightDraftName,
        isCopying,
        isHistoryAction,
        isCrossPeriodCopy,
        performCopyLeftToRight,
    ]);

    const handleUndo = useCallback(async () => {
        if (undoStack.length === 0 || isCopying || isHistoryAction) return;
        const batch = undoStack[undoStack.length - 1];
        if (!batch || batch.targetDraftName !== rightDraftName) return;
        if (batch.deletedSourceEntries.length > 0 && batch.sourceDraftName !== leftDraftName) return;

        setIsHistoryAction(true);
        try {
            for (const entry of batch.entries) {
                await assetManager.deleteScheduleEntry(entry.id);
                setRightEntries((prev) => prev.filter((item) => String(item.id) !== String(entry.id)));
                if (currentDraftName && batch.targetDraftName === currentDraftName) {
                    onRemoveFromCurrentDraft?.(entry.id);
                }
            }
            const restoredSource = batch.deletedSourceEntries.length > 0
                ? await restoreSourceEntries(batch.deletedSourceEntries, batch.sourceDraftName)
                : [];
            setUndoStack((prev) => prev.slice(0, -1));
            setRedoStack((prev) => [...prev, { ...batch, deletedSourceEntries: restoredSource }]);
            onSuccess?.(restoredSource.length > 0 ? 'Undid last copy and restored source entries' : 'Undid last copy');
        } catch (error) {
            console.error('[CopyDraftView] Undo failed:', error);
            onError?.('Failed to undo last copy');
        } finally {
            setIsHistoryAction(false);
        }
    }, [
        undoStack,
        isCopying,
        isHistoryAction,
        rightDraftName,
        leftDraftName,
        assetManager,
        currentDraftName,
        onRemoveFromCurrentDraft,
        restoreSourceEntries,
        onSuccess,
        onError,
    ]);

    const handleRedo = useCallback(async () => {
        if (redoStack.length === 0 || isCopying || isHistoryAction) return;
        const batch = redoStack[redoStack.length - 1];
        if (!batch || batch.targetDraftName !== rightDraftName) return;
        if (batch.deletedSourceEntries.length > 0 && batch.sourceDraftName !== leftDraftName) return;

        setIsHistoryAction(true);
        try {
            const recreatedEntries: ScheduleEntry[] = [];
            for (const entry of batch.entries) {
                const savedEntry = await recreateEntryOnDraft(
                    assetManager,
                    entry,
                    batch.targetDraftName,
                    rightPeriodId
                );
                recreatedEntries.push(savedEntry);
                setRightEntries((prev) => [...prev, savedEntry]);
                if (currentDraftName && batch.targetDraftName === currentDraftName) {
                    onCopyToCurrentDraft?.(savedEntry);
                }
            }
            const lastSaved = recreatedEntries[recreatedEntries.length - 1];
            if (lastSaved) {
                setHighlightedItemId(lastSaved.id);
                window.setTimeout(() => setHighlightedItemId(null), 2000);
            }

            let deletedSourceEntries: ScheduleEntry[] = [];
            if (batch.deletedSourceEntries.length > 0) {
                deletedSourceEntries = await deleteSourceEntries(
                    batch.deletedSourceEntries,
                    batch.sourceDraftName
                );
            }

            setRedoStack((prev) => prev.slice(0, -1));
            setUndoStack((prev) => [...prev, {
                targetDraftName: batch.targetDraftName,
                sourceDraftName: batch.sourceDraftName,
                entries: recreatedEntries,
                deletedSourceEntries,
            }]);
            onSuccess?.(deletedSourceEntries.length > 0 ? 'Redid last copy and removed source entries' : 'Redid last copy');
        } catch (error) {
            console.error('[CopyDraftView] Redo failed:', error);
            onError?.('Failed to redo last copy');
        } finally {
            setIsHistoryAction(false);
        }
    }, [
        redoStack,
        isCopying,
        isHistoryAction,
        rightDraftName,
        leftDraftName,
        rightPeriodId,
        assetManager,
        currentDraftName,
        onCopyToCurrentDraft,
        deleteSourceEntries,
        onSuccess,
        onError,
    ]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
                return;
            }

            if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                void handleUndo();
            } else if (
                (event.ctrlKey && event.key === 'y')
                || ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'z')
            ) {
                event.preventDefault();
                void handleRedo();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo]);

    const applySelectionUpdate = useCallback((
        ids: string[],
        options: { additive?: boolean; replace?: boolean } = {}
    ) => {
        setLeftSelectedIds((prev) => {
            if (options.replace) {
                return ids;
            }
            if (options.additive) {
                const next = new Set(prev);
                ids.forEach((id) => next.add(id));
                return Array.from(next);
            }
            return ids;
        });
    }, []);

    const handleLeftSelect = useCallback((entry: ScheduleEntry, event: React.MouseEvent) => {
        const id = String(entry.id);
        const isToggle = event.metaKey || event.ctrlKey;

        setLeftSelectedIds((prev) => {
            if (isToggle) {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return Array.from(next);
            }
            return [id];
        });
    }, []);

    const handleSourcePanelMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        if ((event.target as Element).closest('[data-schedule-entry-id]')) return;
        if ((event.target as Element).closest('select, button, a, input, textarea, label')) return;

        const container = leftPanelScrollRef.current;
        if (!container) return;

        event.preventDefault();
        marqueeActiveRef.current = false;

        const startX = event.clientX;
        const startY = event.clientY;
        setMarquee({ startX, startY, currentX: startX, currentY: startY });

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = Math.abs(moveEvent.clientX - startX);
            const deltaY = Math.abs(moveEvent.clientY - startY);
            if (deltaX > MARQUEE_DRAG_THRESHOLD_PX || deltaY > MARQUEE_DRAG_THRESHOLD_PX) {
                marqueeActiveRef.current = true;
            }
            setMarquee({
                startX,
                startY,
                currentX: moveEvent.clientX,
                currentY: moveEvent.clientY,
            });
        };

        const handleMouseUp = (upEvent: MouseEvent) => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.removeProperty('user-select');
            setMarquee(null);

            if (marqueeActiveRef.current) {
                const selectRect = normalizeClientRect(startX, startY, upEvent.clientX, upEvent.clientY);
                const ids = findEntryIdsInClientRect(container, selectRect);
                applySelectionUpdate(ids, { additive: upEvent.metaKey || upEvent.ctrlKey });
                return;
            }

            if (!upEvent.metaKey && !upEvent.ctrlKey) {
                setLeftSelectedIds([]);
            }
        };

        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [applySelectionUpdate]);

    const handlePeriodChange = useCallback((side: PanelSide, periodKey: string) => {
        const otherDraftName = side === 'left' ? rightDraftName : leftDraftName;
        const currentDraftNameForSide = side === 'left' ? leftDraftName : rightDraftName;
        const nextDraftName = pickDraftNameForPeriod(
            draftOptions,
            periodKey,
            currentDraftNameForSide,
            otherDraftName
        );
        if (!nextDraftName) return;
        if (side === 'left') {
            setLeftDraftName(nextDraftName);
        } else {
            setRightDraftName(nextDraftName);
        }
    }, [draftOptions, leftDraftName, rightDraftName]);

    const renderDraftSelector = (
        side: PanelSide,
        value: string,
        onChange: (draftName: string) => void,
        otherDraftName: string
    ) => {
        const selectedDraft = draftOptions.find((draft) => draft.draft_name === value) || null;
        const selectedPeriodKey = getPeriodKey(getDraftPeriodId(selectedDraft));
        const versionsForPeriod = draftsByPeriodKey(selectedPeriodKey);

        return (
            <div className="flex flex-col gap-2 mb-3 px-1">
                <div className="flex items-center gap-2">
                    <label htmlFor={`copy-view-period-${side}`} className="text-sm font-medium text-gray-700 whitespace-nowrap w-16">
                        Period
                    </label>
                    <select
                        id={`copy-view-period-${side}`}
                        value={selectedPeriodKey}
                        onChange={(event) => handlePeriodChange(side, event.target.value)}
                        className="flex-1 min-w-0 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {periodOptions.map((period) => (
                            <option key={period.id} value={period.id}>
                                {period.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <label htmlFor={`copy-view-draft-${side}`} className="text-sm font-medium text-gray-700 whitespace-nowrap w-16">
                        Version
                    </label>
                    <select
                        id={`copy-view-draft-${side}`}
                        value={value}
                        onChange={(event) => onChange(event.target.value)}
                        className="flex-1 min-w-0 text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {versionsForPeriod.map((draft) => (
                            <option key={draft.draft_name} value={draft.draft_name}>
                                {formatDraftOptionLabel(draft) || draft.draft_name}
                                {getDraftPeriodId(draft) != null ? '' : ' (unscoped)'}
                            </option>
                        ))}
                    </select>
                    {value === otherDraftName && draftOptions.length > 1 && (
                        <span className="text-xs text-amber-700 whitespace-nowrap">Same draft</span>
                    )}
                </div>
            </div>
        );
    };

    const renderPanel = (
        side: PanelSide,
        draftName: string,
        filteredEntries: ScheduleEntry[],
        selectedIds: string[],
        onSelect: ((entry: ScheduleEntry, event: React.MouseEvent) => void) | null,
        isLoading: boolean,
        onPanelMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void
    ) => (
        <div className="flex-1 min-w-0 flex flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 px-1">
                {side === 'left' ? 'Source' : 'Target'}
            </p>
            {renderDraftSelector(
                side,
                draftName,
                side === 'left' ? setLeftDraftName : setRightDraftName,
                side === 'left' ? rightDraftName : leftDraftName
            )}
            <div
                ref={side === 'left' ? leftPanelScrollRef : rightPanelScrollRef}
                className={`flex-1 overflow-y-auto min-h-0 ${onPanelMouseDown ? 'select-none' : ''}`}
                onScroll={() => handlePanelScroll(side)}
                onMouseDown={onPanelMouseDown}
            >
                {isLoading ? (
                    <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
                        Loading draft…
                    </div>
                ) : (
                    <div className="space-y-2">
                        {activeDays.map((day) => {
                            const itemsForDay = filteredEntries.filter(
                                (item) => item.day === day && item.isScheduled()
                            );
                            return (
                                <DayTableSimple
                                    key={`${side}-${day}-${draftName}`}
                                    day={day}
                                    rooms={allRoomsForGrid}
                                    items={itemsForDay}
                                    dayScheduleEntry={filteredEntries}
                                    user={user}
                                    onOpenModal={() => { }}
                                    onItemDrop={() => { }}
                                    draggedItemInfo={EMPTY_DRAG_INFO}
                                    onGlobalDragStart={() => { }}
                                    onGlobalDragEnd={() => { }}
                                    KEY_SEPARATOR={KEY_SEPARATOR}
                                    teachers={teachers}
                                    groups={groups}
                                    subjects={subjects}
                                    currentWeekNumber={selectedWeek?.week}
                                    currentYear={selectedWeek?.year || selectedYearForView}
                                    showMondayHeader={day === 'monday'}
                                    readOnly
                                    selectionMode={Boolean(onSelect)}
                                    selectedItemIds={onSelect ? selectedIds : undefined}
                                    onItemSelect={onSelect ?? undefined}
                                    highlightedItemId={highlightedItemId}
                                    compact
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    const canCopy = leftSelectedEntries.length > 0 && Boolean(rightDraftName) && leftDraftName !== rightDraftName && !isCopying && !isHistoryAction;
    const canUndo = undoStack.length > 0 && !isCopying && !isHistoryAction;
    const canRedo = redoStack.length > 0 && !isCopying && !isHistoryAction;
    const middleActionButtonClass = 'flex items-center justify-center w-10 h-10 rounded-full shadow-lg transition-colors';
    const historyButtonClass = 'flex items-center justify-center w-8 h-8 rounded-full shadow-lg transition-colors';
    const copyCount = leftSelectedEntries.length;
    const copyButtonTitle = canCopy
        ? (isCrossPeriodCopy
            ? `Copy ${copyCount} selected ${copyCount === 1 ? 'entry' : 'entries'} from another period`
            : `Copy ${copyCount} selected ${copyCount === 1 ? 'entry' : 'entries'} to target draft`)
        : 'Select entries in the source version to copy';
    const crossPeriodCountLabel = `${copyCount} ${copyCount === 1 ? 'entry' : 'entries'}`;

    return (
        <div className="flex flex-col h-full px-2">
            <p className="text-sm text-gray-600 mb-4">
                Choose a period and version on each side. Click to select, ⌘/Ctrl+click to add or remove, or drag to select multiple entries, then copy to the target version.
            </p>
            {marquee && (
                <div
                    className="fixed border-2 border-blue-500 bg-blue-500/20 pointer-events-none z-[100]"
                    style={{
                        left: Math.min(marquee.startX, marquee.currentX),
                        top: Math.min(marquee.startY, marquee.currentY),
                        width: Math.abs(marquee.currentX - marquee.startX),
                        height: Math.abs(marquee.currentY - marquee.startY),
                    }}
                />
            )}
            <SharedScrollProvider>
                <div className="flex flex-1 gap-3 min-h-0 items-stretch">
                    {renderPanel(
                        'left',
                        leftDraftName,
                        leftFilteredEntries,
                        leftSelectedIds,
                        handleLeftSelect,
                        leftLoading,
                        handleSourcePanelMouseDown
                    )}

                    <div className="flex flex-col items-center justify-center gap-2 px-1 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => { handleCopyLeftToRight(); }}
                            disabled={!canCopy}
                            title={copyButtonTitle}
                            aria-label={copyButtonTitle}
                            className={`${middleActionButtonClass} ${canCopy
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                        >
                            <span className="flex flex-col items-center gap-0.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        </button>
                        {isCrossPeriodCopy && (
                            <span className="text-[10px] leading-tight text-amber-700 text-center max-w-[4.5rem]">
                                Different periods
                            </span>
                        )}
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => { void handleUndo(); }}
                                disabled={!canUndo}
                                title="Undo last copy"
                                aria-label="Undo last copy"
                                className={`${historyButtonClass} ${canUndo
                                    ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={() => { void handleRedo(); }}
                                disabled={!canRedo}
                                title="Redo last copy"
                                aria-label="Redo last copy"
                                className={`${historyButtonClass} ${canRedo
                                    ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {renderPanel('right', rightDraftName, rightFilteredEntries, [], null, rightLoading)}
                </div>
            </SharedScrollProvider>
            <Modal
                isOpen={showCrossPeriodConfirm}
                onClose={isCopying ? undefined : () => setShowCrossPeriodConfirm(false)}
                title="Copy from another period?"
                containerClassName="max-w-lg max-h-[80vh]"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                        You are copying {crossPeriodCountLabel} from a different period. Keeping them in the source version can cause collisions if both periods overlap.
                    </p>
                    {isSourceLive && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            The source version is live, so its entries cannot be deleted here. You can still copy and keep them.
                        </p>
                    )}
                    <div className="flex flex-wrap justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setShowCrossPeriodConfirm(false)}
                            disabled={isCopying}
                            title="Cancel copy"
                            aria-label="Cancel copy"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => { void performCopyLeftToRight(false); }}
                            disabled={isCopying}
                            title="Copy entries and keep them in the source version"
                            aria-label="Copy entries and keep them in the source version"
                        >
                            {isCopying ? 'Copying…' : 'Copy and keep source'}
                        </Button>
                        <Button
                            type="button"
                            variant="danger"
                            onClick={() => { void performCopyLeftToRight(true); }}
                            disabled={isCopying || isSourceLive}
                            title={isSourceLive ? 'Live versions cannot be edited' : 'Copy entries and delete them from the source version'}
                            aria-label={isSourceLive ? 'Live versions cannot be edited' : 'Copy entries and delete them from the source version'}
                        >
                            {isCopying ? 'Copying…' : 'Copy and delete source'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CopyDraftView;
